'use client';

import { useEffect, useState } from 'react';

import { COOKIE_CONSENT_CHANGED_EVENT } from '../constants/cookie-constants';
import { cookieStorage } from '../helpers/cookie-helper';

/**
 * Reads one consent category and keeps it in sync with the cookie banner.
 *
 * Two properties matter legally (CIN-14630, § 25 TDDDG) and are the reason this is a hook of its
 * own rather than a read through `useCookies`:
 *
 *  - **No decision means denied.** Only a literal `true` counts as consent, so a visitor who has
 *    not answered the banner yet is treated as having refused.
 *  - **The first render is always denied.** The value starts `false` and is read in an effect, so
 *    server and client agree on the first paint. Reading `document.cookie` during render would
 *    hydrate a gated third-party embed straight into the DOM.
 *
 * Consent changes from the banner are picked up without a reload via COOKIE_CONSENT_CHANGED_EVENT.
 */
export default function useCookieConsent(cookieName: string): boolean {
    const [isGranted, setIsGranted] = useState<boolean>(false);

    useEffect(() => {
        function syncConsent() {
            setIsGranted(cookieStorage.getItem(cookieName) === true);
        }
        syncConsent();
        window.addEventListener(COOKIE_CONSENT_CHANGED_EVENT, syncConsent);
        return () => window.removeEventListener(COOKIE_CONSENT_CHANGED_EVENT, syncConsent);
    }, [cookieName]);

    return isGranted;
}
